import * as React from 'react';

import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';

import { cn } from '../../utils/index';

const buttonVariants = cva(
    'inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:pointer-events-none disabled:bg-gray-300 disabled:border-gray-300 disabled:text-gray-500 active:scale-[0.98]',
    {
        variants: {
            variant: {
                default: 'bg-primary text-white hover:bg-primary-hover',
                outline: 'border border-border text-sub hover:bg-hover',
                outlinePoint: 'border border-point text-point rounded-md font-medium hover:bg-point/5',
                link: 'text-point underline-offset-4 hover:underline',
                ghost: 'hover:bg-hover hover:text-accent-foreground',
            },
            size: {
                default: 'h-10 px-4',
                xs: 'rounded px-2 py-1 text-sm',
                sm: 'h-[34px] rounded-md px-3 text-sm font-medium',
                md: 'h-10 rounded-md py-[10px] pr-4 pl-[14px] text-sm font-medium',
                lg: 'h-11 px-5 text-sm font-medium',
                xl: 'h-12 rounded-lg px-6 text-base max-xs:text-sm font-medium',
                icon: 'w-8 h-8',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    }
);

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : 'button';
        return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
    }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
